import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useTheme } from "next-themes";
import { notificationPreferencesSchema } from "@io-spoke/contracts/notifications";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Switch } from "@components/ui/switch";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import { toast } from "sonner";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "../lib/auth";
import { useUserPreferences } from "@components/layout/UserPreferencesProvider";

type SettingsPageProps = {
  navigate: (route: string) => void;
};

const digestHours = Array.from({ length: 24 }, (_, index) => index);

const appearanceFormSchema = z.object({
  theme: z.enum(["light", "dark"]),
  density: z.enum(["comfortable", "compact"]),
});
type AppearanceFormValues = z.infer<typeof appearanceFormSchema>;

type NotificationFormInput = z.input<typeof notificationPreferencesSchema>;
type NotificationFormValues = z.output<typeof notificationPreferencesSchema>;

export function SettingsPage({ navigate }: SettingsPageProps) {
  const { setTheme: applyTheme } = useTheme();
  const { preferences, isLoading, error, savePreferences } = useUserPreferences();
  const [isSavingAppearance, setIsSavingAppearance] = useState(false);
  const [notificationLoadError, setNotificationLoadError] = useState<string | null>(null);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);

  const appearanceForm = useForm<AppearanceFormValues>({
    resolver: zodResolver(appearanceFormSchema),
    defaultValues: { theme: preferences.theme, density: preferences.density },
  });

  const notificationsForm = useForm<NotificationFormInput, unknown, NotificationFormValues>({
    resolver: zodResolver(notificationPreferencesSchema),
    defaultValues: { teamId: "", roleId: "", inAppEnabled: true, digestEnabled: false, digestHourUtc: 13 },
  });
  const digestEnabledWatched = notificationsForm.watch("digestEnabled");

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/dashboard")}>
              Dashboard
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Settings</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  useEffect(() => {
    appearanceForm.reset({ theme: preferences.theme, density: preferences.density });
  }, [preferences.density, preferences.theme, appearanceForm]);

  useEffect(() => {
    let isActive = true;
    setIsNotificationsLoading(true);
    setNotificationLoadError(null);
    getNotificationPreferences()
      .then((prefs) => {
        if (!isActive) {
          return;
        }
        notificationsForm.reset({
          teamId: prefs.teamId ?? "",
          roleId: prefs.roleId ?? "",
          inAppEnabled: prefs.inAppEnabled,
          digestEnabled: prefs.digestEnabled,
          digestHourUtc: prefs.digestHourUtc,
        });
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load notification preferences.";
        setNotificationLoadError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsNotificationsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    pending: handleSaveAppearancePending,
    callback: handleSaveAppearance,
  } = useAsyncCallback(appearanceForm.handleSubmit(async (values) => {
    setIsSavingAppearance(true);
    try {
      await savePreferences(values);
      toast.success("Appearance settings saved.");
    } catch {
      // Errors are handled in the preferences provider.
    } finally {
      setIsSavingAppearance(false);
    }
  }));

  const {
    pending: handleSaveNotificationsPending,
    callback: handleSaveNotifications,
  } = useAsyncCallback(notificationsForm.handleSubmit(async (values) => {
    try {
      const prefs = await updateNotificationPreferences(values);
      notificationsForm.reset({
        teamId: prefs.teamId ?? "",
        roleId: prefs.roleId ?? "",
        inAppEnabled: prefs.inAppEnabled,
        digestEnabled: prefs.digestEnabled,
        digestHourUtc: prefs.digestHourUtc,
      });
      toast.success("Notification preferences saved.");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to save notification preferences.";
      toast.error(message);
    }
  }));

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Account preferences"
        title="Settings"
        description="Manage your theme, density, and notification settings."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-6">
        {error ? (
          <p className="text-sm text-red-400" role="alert">
            {error}
          </p>
        ) : null}

        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Appearance</CardTitle>
            <CardDescription>Adjust theme and layout density.</CardDescription>
          </CardHeader>
          <form onSubmit={handleSaveAppearance} noValidate>
            <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="settings-theme">Theme</Label>
                <Controller
                  name="theme"
                  control={appearanceForm.control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        applyTheme(value);
                      }}
                    >
                      <SelectTrigger id="settings-theme">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dark">Dark</SelectItem>
                        <SelectItem value="light">Light</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="settings-density">Density</Label>
                <Controller
                  name="density"
                  control={appearanceForm.control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="settings-density">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="comfortable">Comfortable</SelectItem>
                        <SelectItem value="compact">Compact</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button loading={handleSaveAppearancePending} loadingLabel="Saving appearance…"
                type="submit"
                variant="outline"
                disabled={!appearanceForm.formState.isDirty || isSavingAppearance || isLoading}
              >
                Save appearance
              </Button>
            </div>
            </CardContent>
          </form>
        </Card>

        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Notifications</CardTitle>
            <CardDescription>Control how we route alerts and digests.</CardDescription>
          </CardHeader>
          <form onSubmit={handleSaveNotifications} noValidate>
            <CardContent className="space-y-4">
            {notificationLoadError ? (
              <p className="text-sm text-red-400" role="alert">
                {notificationLoadError}
              </p>
            ) : null}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="notify-team">Team</Label>
                <Input
                  id="notify-team"
                  placeholder="support"
                  {...notificationsForm.register("teamId")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notify-role">Role</Label>
                <Input
                  id="notify-role"
                  placeholder="oncall"
                  {...notificationsForm.register("roleId")}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="notify-inapp">In-app notifications</Label>
                <Controller
                  name="inAppEnabled"
                  control={notificationsForm.control}
                  render={({ field }) => (
                    <Switch id="notify-inapp" checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="notify-digest">Daily digest email</Label>
                <Controller
                  name="digestEnabled"
                  control={notificationsForm.control}
                  render={({ field }) => (
                    <Switch id="notify-digest" checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="digest-hour">Digest hour (UTC)</Label>
                <Controller
                  name="digestHourUtc"
                  control={notificationsForm.control}
                  render={({ field }) => (
                    <Select
                      value={String(field.value ?? 0)}
                      onValueChange={(value) => field.onChange(Number(value))}
                      disabled={!digestEnabledWatched}
                    >
                      <SelectTrigger id="digest-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {digestHours.map((hour) => (
                          <SelectItem key={hour} value={String(hour)}>
                            {hour}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button loading={handleSaveNotificationsPending} loadingLabel="Saving notifications…"
                type="submit"
                disabled={isNotificationsLoading || notificationsForm.formState.isSubmitting}
              >
                Save notifications
              </Button>
            </div>
            </CardContent>
          </form>
        </Card>
      </section>
    </PageShell>
  );
}
